import React, { useState, useMemo } from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 15, 30, 45];

/**
 * Bespoke Turf-Green time picker — matches TurfDatePicker style.
 * 24h "HH:MM" string in/out (15-minute granularity).
 *
 * Props:
 *  - value: 'HH:MM' string (24h) or ''
 *  - onChange: (string 'HH:MM') => void
 *  - placeholder: 'Pick time' default
 *  - testId
 *  - className
 */
export function TurfTimePicker({ value, onChange, placeholder = 'Pick time', testId = 'turf-time-picker', className = '' }) {
  const [open, setOpen] = useState(false);

  const { hour24, hour12, minute, period } = useMemo(() => {
    if (!value || !value.includes(':')) {
      return { hour24: null, hour12: null, minute: null, period: 'AM' };
    }
    const [h, m] = value.split(':').map(Number);
    const p = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return { hour24: h, hour12: h12, minute: m, period: p };
  }, [value]);

  const display = value ? `${hour12}:${String(minute).padStart(2, '0')} ${period}` : placeholder;

  const apply = (h12, m, p) => {
    let h24 = h12;
    if (p === 'AM' && h12 === 12) h24 = 0;
    else if (p === 'PM' && h12 !== 12) h24 = h12 + 12;
    onChange(`${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  };

  // Default to 12:00 / AM if nothing chosen yet, else preserve current values
  const curH = hour12 ?? 12;
  const curM = minute ?? 0;
  const curP = period;

  const handleHour = (h) => apply(h, curM, curP);
  const handleMinute = (m) => apply(curH, m, curP);
  const handlePeriod = (p) => apply(curH, curM, p);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`flex items-center gap-2 h-10 px-3 bg-[#171717] border border-[#1A1A1A] hover:border-[#262626] rounded-lg text-[13px] transition-colors text-left w-full ${className}`}
        >
          <Clock className="h-4 w-4 text-[#16A34A] flex-shrink-0" />
          <span className={`flex-1 ${value ? 'text-white font-medium' : 'text-neutral-500'}`}>
            {display}
          </span>
          <ChevronDown className={`h-3.5 w-3.5 text-neutral-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-transparent border-0 shadow-none"
        align="start"
        sideOffset={6}
      >
        <div className="p-3 bg-[#0F0F0F] border border-[#1F1F1F] rounded-xl shadow-2xl shadow-black/40 w-[300px]" data-testid={`${testId}-popover`}>
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-[13px] font-semibold font-heading tracking-tight">
              {value ? display : 'Select time'}
            </span>
            <span className="text-[10px] text-neutral-600 font-body uppercase tracking-wider">15-min</span>
          </div>

          <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
            {/* Hour */}
            <div>
              <div className="text-[9px] uppercase tracking-wider text-neutral-600 font-body text-center mb-1.5">Hour</div>
              <div className="grid grid-cols-3 gap-1 max-h-[200px] overflow-y-auto pr-1 turf-scroll">
                {HOURS_12.map(h => {
                  const sel = hour12 === h;
                  return (
                    <button
                      key={h}
                      type="button"
                      onClick={() => handleHour(h)}
                      data-testid={`${testId}-h-${h}`}
                      className={`h-8 rounded-md text-[12px] font-medium transition-all ${sel ? 'bg-[#16A34A] text-white scale-[1.04]' : 'bg-[#171717] text-neutral-300 hover:bg-[#16A34A]/10 hover:text-[#16A34A]'}`}
                    >
                      {h}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Minute */}
            <div>
              <div className="text-[9px] uppercase tracking-wider text-neutral-600 font-body text-center mb-1.5">Minute</div>
              <div className="grid grid-cols-2 gap-1">
                {MINUTES.map(m => {
                  const sel = minute === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => handleMinute(m)}
                      data-testid={`${testId}-m-${m}`}
                      className={`h-8 rounded-md text-[12px] font-medium transition-all ${sel ? 'bg-[#16A34A] text-white scale-[1.04]' : 'bg-[#171717] text-neutral-300 hover:bg-[#16A34A]/10 hover:text-[#16A34A]'}`}
                    >
                      :{String(m).padStart(2, '0')}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* AM/PM */}
            <div>
              <div className="text-[9px] uppercase tracking-wider text-neutral-600 font-body text-center mb-1.5">Period</div>
              <div className="flex flex-col gap-1">
                {['AM', 'PM'].map(p => {
                  const sel = period === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handlePeriod(p)}
                      data-testid={`${testId}-${p.toLowerCase()}`}
                      className={`h-8 w-12 rounded-md text-[11px] font-bold transition-all ${sel ? 'bg-[#16A34A] text-white' : 'bg-[#171717] text-neutral-400 hover:bg-[#16A34A]/10 hover:text-[#16A34A]'}`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-[#1A1A1A] flex items-center justify-between">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="text-[11px] text-neutral-500 hover:text-red-400 transition-colors font-body"
              data-testid={`${testId}-clear`}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] font-semibold bg-[#16A34A]/10 hover:bg-[#16A34A]/20 text-[#16A34A] border border-[#16A34A]/20 px-3 py-1 rounded-md transition-colors"
              data-testid={`${testId}-done`}
            >
              Done
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default TurfTimePicker;
